import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { FiChevronDown } from "react-icons/fi";
import { TierApplicationCta } from "./DescentApplicationCta";

const formatLabel = (key) =>
  key.replace(/([A-Z])/g, " $1").replace(/^./, (char) => char.toUpperCase());

const TILE_PALETTE = ["#272523", "#1f1e1c", "#181715", "#121110", "#0c0b0a"];

const getTileShade = (index) =>
  TILE_PALETTE[index] ?? TILE_PALETTE[TILE_PALETTE.length - 1];

const hexToRgb = (hex) => {
  const normalized = hex.replace("#", "");
  const value = parseInt(normalized, 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `${r}, ${g}, ${b}`;
};

export const LadderTile = ({
  ladder,
  isLast = false,
  index = 0,
  total = 1,
}) => {
  const {
    title,
    oneLine,
    tribute,
    capacity,
    occupied,
    description,
    ...details
  } = ladder;
  const [isExpanded, setIsExpanded] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const expandableRef = useRef(null);
  const safeOccupied = Math.max(0, Math.min(occupied ?? 0, capacity ?? 0));
  const tileBg = getTileShade(index);
  const tileBgRgb = hexToRgb(tileBg);

  useEffect(() => {
    if (!expandableRef.current) return;
    setContentHeight(expandableRef.current.scrollHeight);
  }, [isExpanded, ladder]);

  return (
    <Tile tileBg={tileBg}>
      <HeaderRow>
        <LevelTitle>{title}</LevelTitle>
        <OneLine>{oneLine}</OneLine>
        <MetaLine>
          <MetaItem>
            {isLast ? `${tribute}+ € / month` : `${tribute} € / month`}
          </MetaItem>
          {capacity ? (
            <>
              <MetaDivider>|</MetaDivider>
              <CapacityDots
                aria-label={`${safeOccupied} of ${capacity} spots occupied`}
              >
                {Array.from({ length: capacity }).map((_, index) => (
                  <CapacityDot
                    key={`${title}-spot-${index}`}
                    filled={index < safeOccupied}
                  />
                ))}
              </CapacityDots>
            </>
          ) : null}
        </MetaLine>
      </HeaderRow>

      <ExpandableArea
        isExpanded={isExpanded}
        contentHeight={contentHeight}
        tileBgRgb={tileBgRgb}
      >
        <ExpandableInner ref={expandableRef}>
          <Description>{description}</Description>

          <DetailsList>
            {Object.entries(details).map(([key, value]) => {
              const isNames = key === "names";
              return (
                <li key={key} data-is-names={isNames}>
                  {isNames ? (
                    <span className="names-key">{`Witness the ${title}`}</span>
                  ) : (
                    <strong>{formatLabel(key)}:</strong>
                  )}
                  {isNames ? (
                    <span className="names-value">
                      {Array.isArray(value)
                        ? value.map((name, index) => (
                            <span key={`${title}-name-${name}-${index}`}>
                              {index > 0 ? (
                                <span className="names-separator">|</span>
                              ) : null}
                              {name}
                            </span>
                          ))
                        : value}
                    </span>
                  ) : (
                    <> {value}</>
                  )}
                </li>
              );
            })}
          </DetailsList>
        </ExpandableInner>
      </ExpandableArea>

      <ChevronButton
        type="button"
        aria-label={isExpanded ? "Collapse details" : "Expand details"}
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded((previous) => !previous)}
      >
        <ChevronDivider isExpanded={isExpanded} />
        <ChevronIcon isExpanded={isExpanded} />
      </ChevronButton>

      <TierApplicationCta tierTitle={title} />
    </Tile>
  );
};

const Tile = styled.p`
  --tile-pad-x: 3rem;
  --tile-pad-bottom: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.14);
  padding: 1.5rem var(--tile-pad-x) var(--tile-pad-bottom) var(--tile-pad-x);
  margin: 4rem 0;
  background: ${({ tileBg }) => tileBg};

  @media screen and (max-width: 600px) {
    --tile-pad-x: 0.8rem;
    --tile-pad-bottom: 2rem;
    padding: 1.5rem var(--tile-pad-x) var(--tile-pad-bottom) var(--tile-pad-x);
  }
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  margin-bottom: 0.8rem;
`;

const LevelTitle = styled.h3`
  font-family: "Ysabeau";
  text-transform: uppercase;
  font-weight: 300;
  margin: 0;
  text-align: center;
  font-size: 1.8rem;
`;

const OneLine = styled.p`
  margin: 0.1rem 0 0.6rem 0;
  text-align: center;
  color: rgba(255, 255, 255, 0.72);
  font-size: 1.2rem;
`;

const MetaLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.7rem;
  flex-wrap: wrap;
`;

const MetaItem = styled.p`
  margin: 0;
  font-weight: 300;
  text-align: center;
  font-size: 1rem;
`;

const MetaDivider = styled.span`
  color: rgba(255, 255, 255, 0.6);
  line-height: 1;
`;

const CapacityDots = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
`;

const CapacityDot = styled.span`
  width: 0.65rem;
  height: 0.65rem;
  border-radius: 50%;
  box-sizing: border-box;
  border: 1px solid rgba(255, 255, 255, 0.9);
  background: ${({ filled }) =>
    filled ? "rgba(255, 255, 255, 0.95)" : "transparent"};
`;

const Description = styled.p`
  margin-top: 0;
  margin-bottom: 0.75rem;
  font-style: italic;
`;

const ExpandableArea = styled.div`
  position: relative;
  max-height: ${({ isExpanded, contentHeight }) =>
    isExpanded ? `${contentHeight}px` : "100px"};
  overflow: hidden;
  transition: max-height 420ms ease;

  @media screen and (max-width: 600px) {
    max-height: ${({ isExpanded, contentHeight }) =>
      isExpanded ? `${contentHeight}px` : "64px"};
  }

  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 6rem;
    pointer-events: none;
    opacity: ${({ isExpanded }) => (isExpanded ? 0 : 1)};
    transition: opacity 280ms ease;
    background: linear-gradient(
      to bottom,
      rgba(${({ tileBgRgb }) => tileBgRgb}, 0) 10%,
      rgba(${({ tileBgRgb }) => tileBgRgb}, 0.5) 50%,
      rgba(${({ tileBgRgb }) => tileBgRgb}, 1) 80%
    );
  }
`;

const ExpandableInner = styled.div`
  padding-bottom: 0.1rem;
`;

const DetailsList = styled.ul`
  margin: 0 2rem 0 0;
  padding-left: 0;
  list-style: none;

  li {
    line-height: 1.9;
    margin-bottom: 0.7rem;
  }

  li:last-child {
    margin-bottom: 0;
  }

  li[data-is-names="true"] {
    text-align: center;
    margin-top: 2.4rem;
    margin-bottom: 1.5rem;
  }

  li[data-is-names="true"] .names-key {
    display: block;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-weight: 400;
  }

  li[data-is-names="true"] .names-value {
    display: block;
    margin-top: 0.35rem;
  }

  li[data-is-names="true"] .names-separator {
    display: inline-block;
    margin: 0 0.8rem;
  }
`;

const ChevronButton = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  box-sizing: border-box;
  margin: 0.85rem auto 0;
  color: rgba(255, 255, 255, 0.85);
`;

const ChevronDivider = styled.span`
  display: block;
  width: 75%;
  height: 1px;
  background: #808080;
  margin-top: ${({ isExpanded }) => (isExpanded ? "1.15rem" : "0")};
  transition: margin-top 260ms ease;

  @media screen and (max-width: 600px) {
    width: 100%;
  }
`;

const ChevronIcon = styled(FiChevronDown)`
  font-size: 1.55rem;
  transition:
    transform 260ms ease,
    opacity 220ms ease;
  transform: rotate(${({ isExpanded }) => (isExpanded ? "180deg" : "0deg")});
  opacity: 0.9;
`;
